import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useApp } from '@/components/AppLayout';
import { SendCreditMemoModal } from '@/components/Modals/SendCreditMemoModal';
import { DeleteCreditMemoModal } from '@/components/DeleteCreditMemoModal';
import { SendHistorySection } from '@/components/SendHistorySection';
import {
  creditMemoApi,
  clientApi,
  taxApi,
  templateConfigApi,
  type CreditMemo,
  type Client,
  type Tax,
  type CreditMemoDeletePreview,
  type LineItem,
} from '@/lib/api';
import { DEFAULT_TEMPLATE_SETTINGS, type HeaderField } from '@/components/TemplateDesigner/types';
import { formatAmount } from '@/lib/currency';
import { getCreditMemoStatusConfig } from '@/lib/creditMemoStatus';
import {
  ArrowLeft,
  ExternalLink,
  Pencil,
  Send,
  Trash2,
  Receipt,
  StickyNote,
  File,
  CreditCard,
  ChevronRight,
  ChevronDown,
  Link,
  Archive,
  Mail,
  MessageSquare,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSendBlockModal } from '@/contexts/SendBlockModalContext';

export function ViewCreditMemo() {
  const { id } = useParams<{ id: string }>();
  const { nav } = useBusinessNav();
  const { currentBusiness, user, isViewOnly } = useApp();
  const { showEmailVerificationModal, showViewOnlyBlockModal } = useSendBlockModal();

  const [loading, setLoading] = useState(true);
  const [creditMemo, setCreditMemo] = useState<CreditMemo | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [error, setError] = useState('');
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePreview, setDeletePreview] = useState<CreditMemoDeletePreview | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editBlockedDialogOpen, setEditBlockedDialogOpen] = useState(false);
  const [editingNumber, setEditingNumber] = useState(false);
  const [editNumberValue, setEditNumberValue] = useState('');
  const [savingNumber, setSavingNumber] = useState(false);
  const [numberError, setNumberError] = useState('');
  const numberInputRef = useRef<HTMLInputElement>(null);
  const [customFieldLabels, setCustomFieldLabels] = useState<Record<string, string>>({});

  const startEditingNumber = () => {
    if (!creditMemo || isViewOnly) return;
    setEditNumberValue(creditMemo.credit_memo_number);
    setNumberError('');
    setEditingNumber(true);
    setTimeout(() => numberInputRef.current?.select(), 0);
  };

  const saveNumber = async () => {
    if (!creditMemo || savingNumber) return;
    const trimmed = editNumberValue.trim();
    if (!trimmed || trimmed === creditMemo.credit_memo_number) {
      setEditingNumber(false);
      setNumberError('');
      return;
    }
    setSavingNumber(true);
    const response = await creditMemoApi.updateNumber(creditMemo.id, trimmed);
    setSavingNumber(false);
    if (response.data) {
      setCreditMemo(response.data);
      setEditingNumber(false);
      setNumberError('');
    } else {
      setNumberError(response.error || 'Failed to update');
    }
  };

  const cancelEditingNumber = () => {
    setEditingNumber(false);
    setNumberError('');
  };

  const handleSendClick = () => {
    if (!user?.is_email_verified) {
      showEmailVerificationModal(user?.email || '');
      return;
    }
    setSendModalOpen(true);
  };

  useEffect(() => {
    if (currentBusiness) {
      clientApi.list(currentBusiness.id).then((response) => {
        if (response.data) setClients(response.data);
      });

      taxApi.list(currentBusiness.id, true).then((response) => { // Include archived for historical display
        if (response.data) setTaxes(response.data);
      });
    }
  }, [currentBusiness]);

  useEffect(() => {
    if (currentBusiness && id) {
      creditMemoApi.get(id).then((response) => {
        if (response.data) {
          setCreditMemo(response.data);
          setLoading(false);
        } else if (response.error) {
          setError(response.error);
          setLoading(false);
        }
      });
    }
  }, [currentBusiness, id]);

  useEffect(() => {
    if (currentBusiness) {
      templateConfigApi.get(currentBusiness.id).then((r) => {
        if (r.data?.settings) {
          const settings = { ...DEFAULT_TEMPLATE_SETTINGS, ...r.data.settings };
          const labels: Record<string, string> = {};
          for (const f of (settings.headerFields || []) as HeaderField[]) {
            if (f.enabled && f.isCustom && f.documentTypes.includes('credit_memo')) {
              labels[f.id] = f.name;
            }
          }
          setCustomFieldLabels(labels);
        }
      }).catch(() => {});
    }
  }, [currentBusiness]);

  // Lock body scroll when component mounts
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const calculateLineItemTax = (item: LineItem) => {
    let taxAmount = 0;
    // Use computed_amount (includes markup) for tax calculation
    const itemAmount = item.computed_amount ?? item.amount;
    (item.tax_ids || []).forEach((taxId: string) => {
      const tax = taxes.find((t) => t.id === taxId);
      if (tax) {
        const taxRate = parseFloat(tax.rate) / 100;
        taxAmount += itemAmount * taxRate;
      }
    });
    return taxAmount;
  };

  const calculateTaxes = () => {
    if (!creditMemo) return {};

    const taxTotals: { [taxId: string]: { name: string; rate: number; amount: number } } = {};

    creditMemo.line_items.forEach(([, items]) => {
      items.forEach((item) => {
        (item.tax_ids || []).forEach((taxId) => {
          const tax = taxes.find((t) => t.id === taxId);
          if (tax) {
            const taxRate = parseFloat(tax.rate) / 100;
            // Use computed_amount (includes markup) for tax calculation
            const itemAmount = item.computed_amount ?? item.amount;
            const taxAmount = itemAmount * taxRate;

            if (!taxTotals[taxId]) {
              taxTotals[taxId] = {
                name: tax.name,
                rate: parseFloat(tax.rate),
                amount: 0,
              };
            }
            taxTotals[taxId].amount += taxAmount;
          }
        });
      });
    });

    return taxTotals;
  };

  const handleMarkAsSent = async () => {
    const response = await creditMemoApi.send(id!, {
      medium: 'marked_by_user',
      destination: '',
      message: '',
      cc_self: false,
    });
    if (response.sendBlocked) {
      if (response.sendBlocked.code === 'email_not_verified') {
        showEmailVerificationModal(response.sendBlocked.email || '');
      } else {
        showViewOnlyBlockModal();
      }
      return;
    }
    if (response.data) {
      setCreditMemo(response.data.credit_memo);
    } else if (response.error) {
      alert(response.error);
    }
  };

  const handleEditClick = () => {
    if (!creditMemo) return;
    if (creditMemo.status === 'allocated') {
      setEditBlockedDialogOpen(true);
    } else {
      nav(`/credit-memos/${id}/edit`);
    }
  };

  const handleApplyCreditMemo = () => {
    nav(`/credit-memos/${id}/apply`);
  };

  const handleDeleteClick = async () => {
    const response = await creditMemoApi.getDeletePreview(id!);
    if (response.data) {
      setDeletePreview(response.data);
      setDeleteModalOpen(true);
    } else if (response.error) {
      alert(response.error);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    const response = await creditMemoApi.delete(id!);
    setIsDeleting(false);

    if (response.error) {
      alert(response.error);
      return;
    }

    setDeleteModalOpen(false);
    nav('/credit-memos');
  };

  const handleArchive = async () => {
    if (!id || !confirm('Archive this credit memo?')) return;
    const response = await creditMemoApi.archive(id);
    if (response.data) {
      setCreditMemo(response.data);
    } else if (response.error) {
      alert(response.error);
    }
  };

  if (loading) return <div className="fixed inset-0 z-50 bg-background" />;

  if (!creditMemo) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col items-center justify-center">
        <p className="text-destructive mb-4">Credit memo not found</p>
        <Button onClick={() => nav('/credit-memos')}>Back to Credit Memos</Button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col items-center justify-center">
        <p className="text-destructive mb-4">{error}</p>
        <Button onClick={() => nav('/credit-memos')}>Back to Credit Memos</Button>
      </div>
    );
  }

  const selectedClient = clients.find((c) => c.id === creditMemo.client);
  const taxTotals = calculateTaxes();
  const subtotal = parseFloat(creditMemo.subtotal);
  const totalMarkup = parseFloat(creditMemo.total_markup || '0');
  const total = parseFloat(creditMemo.total_amount);
  const amountAllocated = parseFloat(creditMemo.amount_allocated || '0');
  const balanceRemaining = parseFloat(creditMemo.balance_remaining ?? creditMemo.total_amount);
  const statusConfig = getCreditMemoStatusConfig(creditMemo.status, `Credit Memo #${creditMemo.credit_memo_number}`);

  const handleCopyLink = () => {
    if (!creditMemo) return;
    const url = `${window.location.origin}/credit-memo/${creditMemo.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const canShareLink = creditMemo.status === 'awaiting_allocation';

  return (
    <>
      <div className="fixed inset-0 z-50 bg-background flex flex-col overflow-hidden">
        <div className="flex-1 flex overflow-hidden">
          {/* Left: Main Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="flex-1 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 flex-shrink-0">
              <button
                onClick={() => nav('/credit-memos')}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Credit Memos
              </button>
              {selectedClient && (
                <>
                  <span className="text-muted-foreground/50">·</span>
                  <button
                    onClick={() => nav(`/clients/${selectedClient.id}`)}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {selectedClient.billing_name}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
              <div className="flex-1" />
              {canShareLink && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                >
                  <Link className="h-4 w-4 mr-1" />
                  Copy Link
                </Button>
              )}
              {!isViewOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditClick}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="max-w-3xl mx-auto space-y-6"
              >
                {/* Title and Status */}
                <div className="flex items-center gap-3">
                  {editingNumber ? (
                    <div className="flex flex-col gap-1 py-0.5">
                      <div className="flex items-center gap-1.5">
                        <Input
                          ref={numberInputRef}
                          value={editNumberValue}
                          onChange={(e) => { setEditNumberValue(e.target.value); setNumberError(''); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveNumber(); if (e.key === 'Escape') cancelEditingNumber(); }}
                          onBlur={saveNumber}
                          className={`h-9 text-lg w-64 ${numberError ? 'border-destructive' : ''}`}
                          disabled={savingNumber}
                        />
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={cancelEditingNumber}
                          className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
                          title="Cancel"
                        >
                          <X className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </div>
                      {numberError && <p className="text-xs text-destructive">{numberError}</p>}
                    </div>
                  ) : (
                    <h1
                      className="text-2xl font-semibold flex items-center gap-2 cursor-pointer hover:text-primary/80 transition-colors group"
                      onClick={startEditingNumber}
                      title="Click to edit"
                    >
                      {creditMemo.credit_memo_number}
                      <Receipt className="h-5 w-5 text-green-500" />
                      <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h1>
                  )}
                  <Badge variant="outline" className={statusConfig.textColorClass}>
                    {statusConfig.label}
                  </Badge>
                </div>

                {/* Client Info */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="p-4 border rounded-lg bg-muted/30"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Client</p>
                      <p className="font-medium">{selectedClient?.billing_name || 'Unknown Client'}</p>
                      {selectedClient?.company_name && (
                        <p className="text-sm text-muted-foreground">{selectedClient.company_name}</p>
                      )}
                    </div>
                    {selectedClient && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => nav(`/clients/${selectedClient.id}`)}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </motion.div>

                {/* Credit Memo Details */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="grid grid-cols-2 gap-4"
                >
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-muted-foreground">Issue Date</p>
                    <p className="font-medium">
                      {creditMemo.credit_memo_date
                        ? new Date(creditMemo.credit_memo_date + 'T00:00:00').toLocaleDateString()
                        : 'Not set'}
                    </p>
                  </div>
                  {creditMemo.status !== 'draft' && (
                    <div className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground">Credit Used</p>
                      <p className="font-medium">
                        {formatAmount(amountAllocated)} of {formatAmount(total)}
                      </p>
                    </div>
                  )}
                  {creditMemo.custom_field_values && Object.entries(creditMemo.custom_field_values).map(([fieldId, value]) => (
                    <div key={fieldId} className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground">{customFieldLabels[fieldId] || fieldId}</p>
                      <p className="font-medium">{value || '—'}</p>
                    </div>
                  ))}
                </motion.div>

                {/* Line Items */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="space-y-4"
                >
                  <Label className="text-muted-foreground">Line Items</Label>
                  <div className="border rounded-lg overflow-hidden">
                    {creditMemo.line_items.map(([sectionName, items], sectionIndex) => (
                      <div key={sectionIndex}>
                        {sectionName && (
                          <div className="bg-green-500/5 border-b px-4 py-2">
                            <span className="text-sm font-medium">{sectionName}</span>
                          </div>
                        )}
                        <table className="w-full">
                          <thead className="bg-muted/50">
                            <tr className="text-xs text-muted-foreground">
                              <th className="text-left px-4 py-2">Item</th>
                              <th className="text-left px-3 py-2 w-16">Type</th>
                              <th className="text-right px-4 py-2 w-20">Qty</th>
                              <th className="text-right px-4 py-2 w-24">Rate</th>
                              <th className="text-right px-4 py-2 w-24">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, itemIndex) => (
                              <tr key={item.id || itemIndex} className="border-t">
                                <td className="px-4 py-3">
                                  <div className="font-medium text-sm">{item.name || '—'}</div>
                                  {item.description && (
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                      {item.description}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-3 text-sm text-muted-foreground">
                                  {item.item_type === 'labor' ? 'Labor' : item.item_type === 'other' ? 'Other' : 'Parts'}
                                </td>
                                <td className="px-4 py-3 text-right text-sm">{item.quantity}</td>
                                <td className="px-4 py-3 text-right text-sm">
                                  <div>{formatAmount(item.computed_rate ?? item.rate)}</div>
                                  {item.tax_ids.length > 0 && (
                                    <div className="text-xs text-muted-foreground">
                                      {taxes.find((t) => t.id === item.tax_ids[0])?.name}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right text-sm">
                                  <div>{formatAmount(item.computed_amount ?? item.amount)}</div>
                                  {item.tax_ids.length > 0 && (
                                    <div className="text-xs text-muted-foreground">
                                      +{formatAmount(calculateLineItemTax(item))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Totals */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="border-t pt-4 space-y-2"
                >
                  {/* 1. Subtotal */}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatAmount(subtotal)}</span>
                  </div>

                  {/* 2. Markup */}
                  {totalMarkup > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Markup</span>
                      <span>{formatAmount(totalMarkup)}</span>
                    </div>
                  )}

                  {/* Taxes */}
                  {Object.keys(taxTotals).length > 0 ? (
                    Object.entries(taxTotals).map(([taxId, tax]) => (
                      <div key={taxId} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {tax.name} ({tax.rate}%)
                        </span>
                        <span>{formatAmount(tax.amount)}</span>
                      </div>
                    ))
                  ) : (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Taxes</span>
                      <span>{formatAmount(0)}</span>
                    </div>
                  )}

                  {/* 5. Credit Total */}
                  <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                    <span>Credit Total</span>
                    <span className="text-green-600">{formatAmount(total)}</span>
                  </div>
                </motion.div>

                {/* Credit Allocations */}
                {creditMemo.allocations && creditMemo.allocations.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="space-y-3"
                  >
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Credit Applied To
                    </Label>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr className="text-xs text-muted-foreground">
                            <th className="text-left px-4 py-2">Document</th>
                            <th className="text-left px-4 py-2">Date Applied</th>
                            <th className="text-right px-4 py-2">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {creditMemo.allocations.map((alloc) => (
                            <tr key={alloc.id} className="border-t">
                              <td className="px-4 py-3 text-sm">
                                {alloc.invoice_number && (
                                  <button
                                    onClick={() => nav(`/invoices/${alloc.invoice}`)}
                                    className="text-primary hover:underline"
                                  >
                                    Invoice #{alloc.invoice_number}
                                  </button>
                                )}
                                {alloc.quote_number && (
                                  <button
                                    onClick={() => nav(`/quotes/${alloc.quote}`)}
                                    className="text-primary hover:underline"
                                  >
                                    Quote #{alloc.quote_number}
                                  </button>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground">
                                {new Date(alloc.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 text-sm text-right font-medium">
                                {formatAmount(alloc.amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </motion.div>
                )}

                {/* Send History */}
                {creditMemo.send_records && creditMemo.send_records.length > 0 && (
                  <SendHistorySection records={creditMemo.send_records} delay={0.4} />
                )}
              </motion.div>
            </div>
          </motion.div>

          {/* Right: Fixed Sidebar */}
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2, type: 'spring', damping: 25 }}
            className="w-72 border-l bg-muted/30 flex flex-col flex-shrink-0"
          >
            {/* Credit Remaining Header */}
            <div className="px-5 py-6 border-b bg-green-500/5">
              <p className="text-sm text-muted-foreground mb-1">Credit Remaining</p>
              <p className={`text-3xl font-bold ${balanceRemaining > 0 ? 'text-green-600' : ''}`}>
                {formatAmount(balanceRemaining)}
              </p>
              {creditMemo.status !== 'draft' && (
                <div className="mt-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Original Amount</span>
                    <span>{formatAmount(total)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Applied</span>
                    <span>{formatAmount(amountAllocated)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-5 space-y-5">
              {/* Attachments */}
              <div className="space-y-2">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <File className="h-4 w-4" />
                  Attachments
                </Label>
                {creditMemo.attachments && creditMemo.attachments.length > 0 ? (
                  <div className="space-y-2">
                    {creditMemo.attachments.map((att, index) => (
                      <a
                        key={index}
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 border rounded-lg bg-background hover:bg-muted/50 transition-colors"
                      >
                        <File className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs truncate">{att.filename}</span>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No attachments</p>
                )}
              </div>

              {/* Note for Client */}
              {creditMemo.note_for_client && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground flex items-center gap-2">
                    <StickyNote className="h-4 w-4" />
                    Note for Client
                  </Label>
                  <p className="text-sm whitespace-pre-wrap bg-background p-3 rounded-lg border">
                    {creditMemo.note_for_client}
                  </p>
                </div>
              )}

              {/* Internal Notes */}
              {creditMemo.internal_notes && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Internal Notes</Label>
                  <p className="text-sm whitespace-pre-wrap bg-yellow-50 dark:bg-yellow-950/30 p-3 rounded-lg border border-yellow-200 dark:border-yellow-900">
                    {creditMemo.internal_notes}
                  </p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="p-4 border-t bg-background space-y-3">
              {!isViewOnly && (
                <>
                  {creditMemo.status === 'draft' && (
                    <Button onClick={handleSendClick} className="w-full">
                      <Send className="h-4 w-4 mr-2" />
                      Send
                    </Button>
                  )}
                  {creditMemo.status === 'awaiting_allocation' && balanceRemaining > 0 && (
                    <Button
                      onClick={handleApplyCreditMemo}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Apply Credit
                    </Button>
                  )}
                  {creditMemo.status === 'allocated' && balanceRemaining > 0 && (
                    <Button
                      onClick={handleApplyCreditMemo}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Apply Remaining Credit
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full">
                        More Actions
                        <ChevronDown className="h-4 w-4 ml-auto" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={handleSendClick}>
                        <Mail className="h-4 w-4 mr-2" />
                        Send via Email
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => console.log('Send via text')}>
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Send via Text
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleMarkAsSent}>
                        <Send className="h-4 w-4 mr-2" />
                        Mark as Sent
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleArchive}>
                        <Archive className="h-4 w-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDeleteClick} className="text-destructive focus:text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <SendCreditMemoModal
        open={sendModalOpen}
        onOpenChange={setSendModalOpen}
        creditMemo={creditMemo}
        client={selectedClient || null}
        business={currentBusiness}
        onSend={async (data) => {
          const response = await creditMemoApi.send(id!, {
            medium: 'email',
            destination: data.to.join(','),
            message: data.message,
            cc_self: data.cc_self,
          });
          if (response.sendBlocked) {
            setSendModalOpen(false);
            if (response.sendBlocked.code === 'email_not_verified') {
              showEmailVerificationModal(response.sendBlocked.email || '');
            } else {
              showViewOnlyBlockModal();
            }
            return;
          }
          if (response.data) {
            setCreditMemo(response.data.credit_memo);
          }
          setSendModalOpen(false);
        }}
      />

      <DeleteCreditMemoModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        creditMemoNumber={creditMemo.credit_memo_number}
        preview={deletePreview}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />

      {/* Edit Blocked Dialog */}
      <AlertDialog open={editBlockedDialogOpen} onOpenChange={setEditBlockedDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Edit Credit Memo</AlertDialogTitle>
            <AlertDialogDescription>
              This credit memo cannot be edited because it has been fully allocated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setEditBlockedDialogOpen(false)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
